import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Plus,
  RefreshCw,
  Eye,
  Edit,
  Sparkles,
  Send,
  Upload,
  BarChart3,
  Award,
  FileText,
  Search,
  Filter,
  MoreHorizontal,
  ChevronRight,
} from 'lucide-react';

import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

import SourcingRfpService from '../../services/sourcingRFPService';

//////////////////////////////////////////////////////////////
// ACTION DROPDOWN COMPONENT (Updated Version)
//////////////////////////////////////////////////////////////

const STATUS_LABELS = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  EVALUATION: 'Evaluation',
  AWARDED: 'Awarded',
  CANCELLED: 'Cancelled',
};

const STATUS_TO_STAGE = {
  DRAFT: 'Drafting',
  PUBLISHED: 'Live Bidding',
  EVALUATION: 'Evaluation',
  AWARDED: 'Awarded',
  CANCELLED: 'Closed',
};

const ACTION_KEYS = {
  VIEW: 'view',
  EDIT: 'edit',
  GENERATE_AI: 'generate-ai',
  SUBMIT_APPROVAL: 'submit-approval',
  PUBLISH: 'publish',
  EVALUATE: 'evaluate',
  AWARD: 'award',
  CONVERT_CONTRACT: 'convert-contract',
};

const ALLOWED_ACTIONS_BY_STATUS = {
  DRAFT: [
    ACTION_KEYS.VIEW,
    ACTION_KEYS.EDIT,
    ACTION_KEYS.GENERATE_AI,
    ACTION_KEYS.SUBMIT_APPROVAL,
    ACTION_KEYS.PUBLISH,
  ],
  PUBLISHED: [ACTION_KEYS.VIEW, ACTION_KEYS.EVALUATE],
  EVALUATION: [ACTION_KEYS.VIEW, ACTION_KEYS.AWARD],
  AWARDED: [ACTION_KEYS.VIEW, ACTION_KEYS.CONVERT_CONTRACT],
  CANCELLED: [ACTION_KEYS.VIEW],
};

const STAGE_BADGE_STYLES = {
  Drafting: { bg: '#eef2ff', color: '#4338ca', dot: '#4338ca' },
  'Live Bidding': { bg: '#dbeafe', color: '#1d4ed8', dot: '#1d4ed8' },
  Evaluation: { bg: '#fef3c7', color: '#b45309', dot: '#b45309' },
  Awarded: { bg: '#dcfce7', color: '#166534', dot: '#166534' },
  Closed: { bg: '#fee2e2', color: '#b91c1c', dot: '#b91c1c' },
};

const STATUS_BADGE_STYLES = {
  DRAFT: { bg: '#f8fafc', color: '#475569', dot: '#64748b' },
  PUBLISHED: { bg: '#e0f2fe', color: '#0369a1', dot: '#0284c7' },
  EVALUATION: { bg: '#fef3c7', color: '#b45309', dot: '#b45309' },
  AWARDED: { bg: '#dcfce7', color: '#166534', dot: '#16a34a' },
  CANCELLED: { bg: '#fee2e2', color: '#b91c1c', dot: '#dc2626' },
};

const normalizeStatus = (status) => String(status || '').toUpperCase();

const getStageFromStatus = (status) => {
  const normalized = normalizeStatus(status);
  return STATUS_TO_STAGE[normalized] || 'Drafting';
};

const ActionDropdown = ({ rfp, getAllowedActions, onAction }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  // Close on outside click or scroll
  useEffect(() => {
    const close = (e) => {
      if (
        !menuRef.current?.contains(e.target) &&
        !buttonRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', close);
      window.addEventListener('scroll', close, true);
    }

    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  const rect = buttonRef.current?.getBoundingClientRect();

  const actions = getAllowedActions(rfp);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className={`flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 transition-all duration-200 shadow-sm ${open ? 'bg-gray-100 border-gray-400' : 'bg-white hover:bg-gray-50'}`}
      >
        <MoreHorizontal
          size={16}
          className={open ? 'text-blue-600' : 'text-gray-600'}
        />
      </button>

      {open &&
        rect &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: rect.bottom + 6,
              left: rect.left - 144,
              zIndex: 9999,
            }}
            className="w-48 bg-white border border-gray-200 rounded-lg shadow-xl py-1"
          >
            {actions.map((action) => (
              <MenuItem
                key={action.key}
                icon={action.icon}
                label={action.label}
                onClick={() => {
                  setOpen(false);
                  onAction(action.key, rfp);
                }}
              />
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

///////////////////////////////////////////////////////////
// MENU ITEM HELPER
///////////////////////////////////////////////////////////

const MenuItem = ({ icon, label, onClick, danger }) => (
  <button
    onClick={(e) => {
      e.stopPropagation(); // Prevent row clicks if any
      onClick();
    }}
    className={`
      flex items-center gap-2
      w-full px-3 py-2 text-sm
      transition-colors duration-150
      ${
        danger
          ? 'text-red-600 hover:bg-red-50'
          : 'text-gray-700 hover:bg-gray-100'
      }
    `}
  >
    <span className={danger ? 'text-red-500' : 'text-gray-400'}>{icon}</span>
    {label}
  </button>
);

//////////////////////////////////////////////////////////////
// MAIN COMPONENT
//////////////////////////////////////////////////////////////

const RfpManagement = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [rfps, setRfps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [projectTypeFilter, setProjectTypeFilter] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const [pagination, setPagination] = useState({
    page: 1,
    itemsPerPage: 10,
    totalItems: 0,
    pages: 1,
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadRfps = React.useCallback(async (targetPage, targetStatus) => {
    // Determine values to use
    const pageToFetch = targetPage !== undefined ? targetPage : pagination.page;
    const statusToFetch = targetStatus !== undefined ? targetStatus : statusFilter;

    setLoading(true);

    try {
      const params = {
        page: pageToFetch,
        limit: pagination.itemsPerPage,
      };

      if (statusToFetch) {
        params.status = statusToFetch;
      }

      const response = await SourcingRfpService.getRfps(params);

      setRfps(response.data || []);

      if (response.pagination) {
        setPagination((prev) => ({
          ...prev,
          page: pageToFetch,
          totalItems: response.pagination.total,
          pages: response.pagination.pages,
        }));
      }
    } catch (err) {
      showError(err.message || 'Failed to load RFPs');
    } finally {
      setLoading(false);
    }
  }, [pagination.itemsPerPage, showError]); // pagination.itemsPerPage is usually constant, and showError is stable.

  // Fetch only on mount and when pagination.page changes
  // Filters will trigger a fetch via explicit Apply button or can be added here if desired.
  useEffect(() => {
    loadRfps(pagination.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]); 

  const handleView = (rfp) => {
    navigate(`/sourcing/rfps/processing/${rfp._id}`);
  };

  const handleEdit = (rfp) => {
    navigate(`/sourcing/rfps/edit/${rfp._id}`);
  };

  const handlePublish = async (rfp) => {
    if (
      !window.confirm(
        `Are you sure you want to publish "${rfp.title}"? Once published, vendors can see it.`,
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await SourcingRfpService.publishRfp(rfp._id);
      success(`RFP "${rfp.title}" has been published successfully!`);
      loadRfps();
    } catch (err) {
      showError(err.message || 'Failed to publish RFP');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEvaluation = async (rfp) => {
    if (
      !window.confirm(
        'Move this RFP to Evaluation? This signifies bidding is closed and scoring has begun.',
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await SourcingRfpService.startEvaluation(rfp._id);
      success(`RFP "${rfp.title}" is now in Evaluation.`);
      loadRfps();
    } catch (err) {
      const errorMsg = err.message || 'Could not start evaluation';
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const renderBadge = (styleConfig, label) => (
    <span
      className="badge-premium"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 14px',
        borderRadius: '50px',
        fontSize: '12px',
        fontWeight: 600,
        background: styleConfig.bg,
        color: styleConfig.color,
        border: `1px solid ${styleConfig.color}30`,
        boxShadow: `0 2px 4px ${styleConfig.color}15`,
        transition: 'all 0.2s ease',
        whiteSpace: 'nowrap'
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: styleConfig.dot,
          boxShadow: `0 0 0 2px ${styleConfig.dot}30`
        }}
      />
      {label}
    </span>
  );

  const getStageBadge = (status) => {
    const stage = getStageFromStatus(status);
    const styleConfig =
      STAGE_BADGE_STYLES[stage] || STAGE_BADGE_STYLES.Drafting;
    return renderBadge(styleConfig, stage);
  };

  const getStatusBadge = (status) => {
    const normalized = normalizeStatus(status);
    const styleConfig =
      STATUS_BADGE_STYLES[normalized] || STATUS_BADGE_STYLES.DRAFT;
    const label = STATUS_LABELS[normalized] || STATUS_LABELS.DRAFT;
    return renderBadge(styleConfig, label);
  };

  const projectTypeOf = (rfp) => rfp?.projectType || rfp?.category || 'General';

  const getAllowedActions = (rfp) => {
    const status = normalizeStatus(rfp?.status);
    const allowed = ALLOWED_ACTIONS_BY_STATUS[status] || [ACTION_KEYS.VIEW];

    const actionCatalog = {
      [ACTION_KEYS.VIEW]: {
        key: ACTION_KEYS.VIEW,
        label: 'View',
        icon: <Eye size={16} />,
      },
      [ACTION_KEYS.EDIT]: {
        key: ACTION_KEYS.EDIT,
        label: 'Edit',
        icon: <Edit size={16} />,
      },
      [ACTION_KEYS.GENERATE_AI]: {
        key: ACTION_KEYS.GENERATE_AI,
        label: 'Generate AI',
        icon: <Sparkles size={16} />,
      },
      [ACTION_KEYS.SUBMIT_APPROVAL]: {
        key: ACTION_KEYS.SUBMIT_APPROVAL,
        label: 'Submit for Approval',
        icon: <Send size={16} />,
      },
      [ACTION_KEYS.PUBLISH]: {
        key: ACTION_KEYS.PUBLISH,
        label: 'Publish',
        icon: <Upload size={16} />,
      },
      [ACTION_KEYS.EVALUATE]: {
        key: ACTION_KEYS.EVALUATE,
        label: 'Evaluate',
        icon: <BarChart3 size={16} />,
      },
      [ACTION_KEYS.AWARD]: {
        key: ACTION_KEYS.AWARD,
        label: 'Award',
        icon: <Award size={16} />,
      },
      [ACTION_KEYS.CONVERT_CONTRACT]: {
        key: ACTION_KEYS.CONVERT_CONTRACT,
        label: 'Convert to Contract',
        icon: <FileText size={16} />,
      },
    };

    return allowed.map((actionKey) => actionCatalog[actionKey]).filter(Boolean);
  };

  const isActionAllowed = (actionKey, rfp) => {
    const status = normalizeStatus(rfp?.status);
    const allowed = ALLOWED_ACTIONS_BY_STATUS[status] || [ACTION_KEYS.VIEW];
    return allowed.includes(actionKey);
  };

  const handleAction = async (actionKey, rfp) => {
    if (!isActionAllowed(actionKey, rfp)) {
      showError('This action is not allowed at the current lifecycle stage.');
      return;
    }

    if (actionKey === ACTION_KEYS.VIEW) {
      handleView(rfp);
      return;
    }

    if (actionKey === ACTION_KEYS.EDIT) {
      handleEdit(rfp);
      return;
    }

    if (actionKey === ACTION_KEYS.GENERATE_AI) {
      showError(
        'Future Scope: AI generation is deferred. Opening AI RFP Input shell.',
      );
      navigate('/sourcing/rfps/create/ai-input', {
        state: { source: 'rfp-registry', rfpId: rfp._id },
      });
      return;
    }

    if (actionKey === ACTION_KEYS.SUBMIT_APPROVAL) {
      success('RFP submitted for approval queue.');
      return;
    }

    if (actionKey === ACTION_KEYS.PUBLISH) {
      await handlePublish(rfp);
      return;
    }

    if (actionKey === ACTION_KEYS.EVALUATE) {
      await handleStartEvaluation(rfp);
      navigate(`/sourcing/evaluation-award?tab=evaluation&rfpId=${rfp._id}`);
      return;
    }

    if (actionKey === ACTION_KEYS.AWARD) {
      navigate(`/sourcing/evaluation-award?tab=award&rfpId=${rfp._id}`);
      return;
    }

    if (actionKey === ACTION_KEYS.CONVERT_CONTRACT) {
      navigate(`/sourcing/evaluation-award?tab=award&rfpId=${rfp._id}`);
    }
  };

  const filteredRfps = useMemo(() => {
    return rfps.filter((rfp) => {
      const stage = getStageFromStatus(rfp?.status);
      const normalizedStatus = normalizeStatus(rfp?.status);
      const projectType = projectTypeOf(rfp);
      const searchable =
        `${rfp?.title || ''} ${rfp?.rfpNumber || ''}`.toLowerCase();

      const matchesSearch =
        !debouncedSearchTerm || searchable.includes(debouncedSearchTerm.trim().toLowerCase());
      const matchesStage = !stageFilter || stage === stageFilter;
      const matchesStatus = !statusFilter || normalizedStatus === statusFilter;
      const matchesProjectType =
        !projectTypeFilter || projectType === projectTypeFilter;

      return (
        matchesSearch && matchesStage && matchesStatus && matchesProjectType
      );
    });
  }, [rfps, debouncedSearchTerm, stageFilter, statusFilter, projectTypeFilter]);

  const stageOptions = useMemo(() => Object.values(STATUS_TO_STAGE), []);

  const statusOptions = useMemo(
    () =>
      Object.keys(STATUS_LABELS).map((key) => ({
        value: key,
        label: STATUS_LABELS[key],
      })),
    [],
  );

  const projectTypeOptions = useMemo(() => {
    const set = new Set(rfps.map((rfp) => projectTypeOf(rfp)));
    return Array.from(set).sort();
  }, [rfps]);

  const columns = [
    {
      key: 'rfpNumber',
      label: 'RFP ID',
      render: (_, rfp) => (
        <span style={{ fontWeight: 600 }}>{rfp.rfpNumber || '-'}</span>
      ),
    },
    {
      key: 'title',
      label: 'Title',
      render: (_, rfp) => (
        <span style={{ fontWeight: 600 }}>{rfp.title || '-'}</span>
      ),
    },
    {
      key: 'projectType',
      label: 'Project Type',
      align: 'center',
      render: (_, rfp) => projectTypeOf(rfp),
    },
    {
      key: 'createdBy',
      label: 'Created By',
      align: 'center',
      render: (_, rfp) => {
        if (rfp?.createdBy?.name) return rfp.createdBy.name;
        if (rfp?.createdBy?.email) return rfp.createdBy.email;
        if (typeof rfp?.createdBy === 'string') return rfp.createdBy.slice(-8);
        return '-';
      },
    },
    {
      key: 'createdAt',
      label: 'Created Date',
      align: 'center',
      render: (_, rfp) =>
        rfp.createdAt ? new Date(rfp.createdAt).toLocaleDateString() : '-',
    },
    {
      key: 'stage',
      label: 'Stage',
      render: (_, rfp) => getStageBadge(rfp.status),
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, rfp) => getStatusBadge(rfp.status),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (_, rfp) => (
        <ActionDropdown
          rfp={rfp}
          getAllowedActions={getAllowedActions}
          onAction={handleAction}
        />
      ),
    },
  ];

  const headerActions = [
    {
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      variant: 'outline',
      onClick: loadRfps,
    },
    {
      label: 'Create / Generate RFP',
      icon: <Plus size={18} />,
      variant: 'primary',
      onClick: () => navigate('/sourcing/rfps/create'),
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="RFP Registry"
        description="Entry point to track, process, evaluate, award and convert sourcing requests."
        actions={headerActions}
        className="mb-4"
      />

      <div className="page-container px-4 pb-4">
        <div
          className="filter-section-container mb-4"
          style={{ 
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}
        >
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center gap-2">
              <Filter size={20} className="text-primary" />
              <h5 className="mb-0 font-weight-bold text-dark" style={{ fontSize: '1.1rem', letterSpacing: '-0.01em' }}>Filter & Search RFPs</h5>
            </div>
            <div className="d-flex align-items-center gap-3">
              <button
                className="btn btn-link btn-sm text-decoration-none p-0 text-secondary hover-text-primary"
                style={{ fontWeight: 500, fontSize: '14px' }}
                onClick={() => {
                  setSearchTerm('');
                  setStageFilter('');
                  setStatusFilter('');
                  setProjectTypeFilter('');
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
              >
                Reset All Filters
              </button>
              <button
                className="btn btn-primary btn-sm px-4"
                style={{ borderRadius: '8px', height: '36px', fontWeight: 600 }}
                onClick={() => loadRfps(1, statusFilter)}
              >
                Apply Filters
              </button>
            </div>
          </div>

          <div 
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1fr 1fr 1fr',
              gap: '20px',
              alignItems: 'end'
            }}
            className="filter-grid-container"
          >
            <div style={{ minWidth: '0' }}>
              <label className="form-label mb-2 text-muted small font-weight-bold uppercase tracking-wider" style={{ fontSize: '11px' }}>Search Title / ID</label>
              <div className="position-relative shadow-sm transition-all rounded-lg">
                <Search 
                  size={18} 
                  className="position-absolute" 
                  style={{ left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', zIndex: 10 }}
                />
                <input
                  className="form-control"
                  style={{ 
                    paddingLeft: '44px', 
                    height: '44px', 
                    borderRadius: '10px', 
                    border: '1px solid #cbd5e1',
                    fontSize: '0.92rem',
                    boxShadow: 'none',
                    backgroundColor: '#ffffff'
                  }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Find RFP by number or title..."
                />
              </div>
            </div>

            <div style={{ minWidth: '0' }}>
              <label className="form-label mb-2 text-muted small font-weight-bold uppercase tracking-wider" style={{ fontSize: '11px' }}>Lifecycle Stage</label>
              <select
                className="form-select shadow-sm"
                style={{ 
                  height: '44px', 
                  borderRadius: '10px', 
                  border: '1px solid #cbd5e1',
                  fontSize: '0.92rem',
                  paddingLeft: '12px',
                  backgroundColor: '#ffffff'
                }}
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
              >
                <option value="">All Stages</option>
                {stageOptions.map((stage) => (
                  <option key={stage} value={stage}>
                    {stage}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ minWidth: '0' }}>
              <label className="form-label mb-2 text-muted small font-weight-bold uppercase tracking-wider" style={{ fontSize: '11px' }}>Current Status</label>
              <select
                className="form-select shadow-sm"
                style={{ 
                  height: '44px', 
                  borderRadius: '10px', 
                  border: '1px solid #cbd5e1',
                  fontSize: '0.92rem',
                  paddingLeft: '12px',
                  backgroundColor: '#ffffff'
                }}
                value={statusFilter}
                onChange={(e) => {
                  setPagination((prev) => ({ ...prev, page: 1 }));
                  setStatusFilter(e.target.value);
                }}
              >
                <option value="">All Statuses</option>
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ minWidth: '0' }}>
              <label className="form-label mb-2 text-muted small font-weight-bold uppercase tracking-wider" style={{ fontSize: '11px' }}>Project Category</label>
              <select
                className="form-select shadow-sm"
                style={{ 
                  height: '44px', 
                  borderRadius: '10px', 
                  border: '1px solid #cbd5e1',
                  fontSize: '0.92rem',
                  paddingLeft: '12px',
                  backgroundColor: '#ffffff'
                }}
                value={projectTypeFilter}
                onChange={(e) => setProjectTypeFilter(e.target.value)}
              >
                <option value="">All Project Types</option>
                {projectTypeOptions.map((projectType) => (
                  <option key={projectType} value={projectType}>
                    {projectType}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div
          className="card shadow-sm mb-4"
          style={{ 
            border: '1px solid #e2e8f0', 
            borderRadius: '12px',
            overflow: 'hidden'
          }}
        >
          <div className="card-body p-0">
            <DataTable
              data={filteredRfps}
              columns={columns}
              loading={loading}
              showActions={false}
              searchable={false}
              showFilterButton={false}
              className="border-0"
              onRowClick={(row) =>
                navigate(`/sourcing/rfps/processing/${row._id}`)
              }
              pagination={{
                currentPage: pagination.page,
                totalPages: pagination.pages,
                total: pagination.totalItems,
                itemsPerPage: pagination.itemsPerPage,
              }}
              onPageChange={(page) =>
                setPagination((prev) => ({ ...prev, page }))
              }
              emptyMessage={
                <div className="text-center py-5">
                  <div className="mb-3">
                    <FileText size={48} className="text-muted opacity-25" />
                  </div>
                  <h5 className="text-dark font-weight-bold">No RFPs found</h5>
                  <p className="text-muted mb-4">Try adjusting your filters or create a new sourcing request.</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => navigate('/sourcing/rfps/create')}
                  >
                    <Plus size={18} className="mr-2" />
                    Create New RFP
                  </button>
                </div>
              }
            />
          </div>
        </div>

        <div
          className="d-flex justify-content-end mt-2 text-muted"
          style={{ fontSize: '12px' }}
        >
          <span className="d-inline-flex align-items-center gap-1">
            Click any row to open RFP Processing
            <ChevronRight size={14} />
          </span>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default RfpManagement;
